import { useCallback, useMemo } from 'react';
import { AutoPricingUnderCutModeSelector } from 'src/components/AutoPricing/AutoPricingUnderCutModeSelector/AutoPricingUnderCutModeSelector';
import { AutoPricingUnderCutReferenceListingPositionInput } from 'src/components/AutoPricing/AutoPricingUnderCutReferenceListingPositionInput/AutoPricingUnderCutReferenceListingPositionInput';
import { Content } from 'src/contexts/ContentContext';
import { PosCurrencyField } from 'src/core/POS/PosCurrencyField';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosTextField } from 'src/core/POS/PosTextField';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { ToggleGroup } from 'src/core/ui/ToggleGroup';
import { AutoPricingSettingsInput } from 'src/dialogs/BulkEdits/BulkEditAutoPricingSettingsDialog/BulkEditAutoPricingSettingsDialog.types';
import { useAutoPricingEnabled } from 'src/dialogs/BulkEdits/BulkEditAutoPricingSettingsDialog/components/AutoPricingEnabled';
import { PricingSettingStatus } from 'src/dialogs/BulkEdits/BulkEditAutoPricingSettingsDialog/components/PricingSetting';
import { useAutoPricingSettings } from 'src/dialogs/BulkEdits/BulkEditAutoPricingSettingsDialog/useBulkAutoPricingSettings';
import { useUndercutAmountSettings } from 'src/hooks/useUndercutAmountSettings';
import { WarningOutlineIcon } from 'src/svgs/Viagogo';
import {
  getUndercutPriceRepresentingMode,
  UNDERCUT_PRICE_MODE_OPTIONS,
  UndercutPriceSettingsMode,
} from 'src/utils/autoPricingUtils';
import { ContentId } from 'src/utils/constants/contentId';
import { roundToPrecision } from 'src/utils/numberFormatter';
import { AutoPricingUndercutMode } from 'src/WebApiController';

import * as styles from './BulkEditAutoPricingSettingsMobileContent.css';

type AutoPricingSettingsProps = {
  settingsInput: AutoPricingSettingsInput;
  canUseZoneMap?: boolean;
};

export const MobileAutoPricingSettings = ({
  settingsInput,
}: AutoPricingSettingsProps) => {
  const hasConflict = useMemo(
    () => Object.values(settingsInput).some(({ hasConflict }) => hasConflict),
    [settingsInput]
  );

  const {
    header,
    settings,
    hasConflict: hasAutoPricingEnableConflict,
    hasChange,
    onReset,
  } = useAutoPricingEnabled(settingsInput);

  const {
    uiCurrency,
    undercutMode,
    undercutRelativeAmount,
    undercutAbsoluteAmount,
    undercutReferenceListingPosition,
    onUndercutModeChange,
    onUndercutChange,
  } = useAutoPricingSettings();

  const undercutModeHasChange = useMemo(() => {
    return (
      settingsInput.undercutMode?.value !== undercutMode?.value ||
      settingsInput.undercutReferenceListingPosition?.value !==
        undercutReferenceListingPosition?.value
    );
  }, [
    settingsInput.undercutMode?.value,
    undercutMode?.value,
    settingsInput.undercutReferenceListingPosition?.value,
    undercutReferenceListingPosition?.value,
  ]);

  const showReferenceListingPositionInput = useMemo(() => {
    return (
      undercutModeHasChange &&
      (undercutMode?.value !== AutoPricingUndercutMode.Simple ||
        !!undercutReferenceListingPosition?.value)
    );
  }, [
    undercutMode?.value,
    undercutModeHasChange,
    undercutReferenceListingPosition?.value,
  ]);

  const onResetUndercutMode = useCallback(() => {
    onUndercutModeChange({
      undercutModeNew: settingsInput.undercutMode?.value,
      undercutModeHasChangedNew: false,
      undercutReferenceListingPositionNew:
        settingsInput.undercutReferenceListingPosition?.value,
      undercutReferenceListingPositionHasChangedNew: false,
    });
  }, [
    onUndercutModeChange,
    settingsInput.undercutMode?.value,
    settingsInput.undercutReferenceListingPosition?.value,
  ]);

  const {
    undercutAbsoluteAmountMode,
    setUndercutAbsoluteAmountMode,
    undercutRelativeAmountMode,
    setUndercutRelativeAmountMode,
  } = useUndercutAmountSettings(
    undercutAbsoluteAmount?.value,
    undercutRelativeAmount?.value
  );

  const undercutAmountHasChange = useMemo(
    () =>
      settingsInput.undercutAbsoluteAmount?.value !==
        undercutAbsoluteAmount?.value ||
      settingsInput.undercutRelativeAmount?.value !==
        undercutRelativeAmount?.value,
    [
      settingsInput.undercutAbsoluteAmount?.value,
      settingsInput.undercutRelativeAmount?.value,
      undercutAbsoluteAmount?.value,
      undercutRelativeAmount?.value,
    ]
  );
  const onResetUndercutAmount = useCallback(
    () =>
      onUndercutChange(
        undefined,
        settingsInput.undercutAbsoluteAmount?.value,
        settingsInput.undercutRelativeAmount?.value
      ),
    [
      onUndercutChange,
      settingsInput.undercutAbsoluteAmount?.value,
      settingsInput.undercutRelativeAmount?.value,
    ]
  );

  const undercuttingSettings = useMemo<MobilePricingSettingProps[]>(() => {
    const settings: MobilePricingSettingProps[] = [
      {
        header: <Content id={ContentId.SetReferenceListingTo} />,
        hasChange: undercutModeHasChange,
        hasConflict:
          settingsInput.undercutMode?.hasConflict ||
          settingsInput.undercutReferenceListingPosition?.hasConflict,
        onReset: onResetUndercutMode,
        details: (
          <AutoPricingUnderCutModeSelector
            undercutMode={undercutMode?.value ?? null}
            undercutReferenceListingPosition={
              undercutReferenceListingPosition?.value ?? null
            }
            setUndercutModeValues={(
              newMode: AutoPricingUndercutMode | null,
              newPosition: number | null
            ) => {
              onUndercutModeChange({
                undercutModeNew: newMode,
                undercutReferenceListingPositionNew: newPosition,
              });
            }}
            rootStyle={{ width: '100%' }}
            disabled={false}
          />
        ),
      },
    ];

    if (showReferenceListingPositionInput) {
      settings.push({
        header: <Content id={ContentId.ReferenceThe} />,
        details: (
          <AutoPricingUnderCutReferenceListingPositionInput
            undercutMode={undercutMode?.value ?? null}
            undercutReferenceListingPosition={
              undercutReferenceListingPosition?.value ?? null
            }
            setUndercutReferenceListingPosition={(
              newPosition: number | null
            ) => {
              onUndercutModeChange({
                undercutModeNew: undefined,
                undercutReferenceListingPositionNew: newPosition,
              });
            }}
            disabled={false}
            direction={'column'}
            rootStyle={{ width: '100%' }}
          />
        ),
      });
    }

    settings.push({
      header: <Content id={ContentId.HowToDeterminePrice} />,
      hasChange: undercutAmountHasChange,
      hasConflict:
        settingsInput.undercutAbsoluteAmount?.hasConflict ||
        settingsInput.undercutRelativeAmount?.hasConflict,
      onReset: onResetUndercutAmount,
      details: (
        <Stack direction="column" width="full" gap="m">
          <Stack width="full" gap="m">
            <ToggleGroup
              disabled={!undercutMode || !undercutRelativeAmount?.value}
              options={UNDERCUT_PRICE_MODE_OPTIONS}
              value={undercutRelativeAmountMode}
              onValueChange={(value) => {
                if (!value) return;

                const mode = value as UndercutPriceSettingsMode;
                setUndercutRelativeAmountMode(mode);

                if (undercutRelativeAmount?.value) {
                  const curAmountAbs = Math.abs(undercutRelativeAmount.value);
                  onUndercutChange(
                    undefined,
                    undefined,
                    getUndercutPriceRepresentingMode(curAmountAbs, mode)
                  );
                }
              }}
            />

            <div className={styles.inlinePricingInputText}>
              <Content id={ContentId.PriceBy} />
            </div>

            <PosFormField style={{ width: 'fit-content' }}>
              <PosTextField
                disabled={!undercutMode}
                value={
                  undercutRelativeAmount?.value != null
                    ? roundToPrecision(
                        Math.abs(undercutRelativeAmount.value) * 100,
                        8
                      )
                    : ''
                }
                placeholder={undefined}
                type="number"
                postfixDisplay="%"
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  if (v >= 0 && v <= Number.MAX_VALUE) {
                    const relativeValue = Math.min(v, 100) / 100;
                    if (
                      undercutRelativeAmountMode ===
                      UndercutPriceSettingsMode.Premium
                    ) {
                      onUndercutChange(undefined, undefined, -relativeValue);
                    } else {
                      onUndercutChange(undefined, undefined, relativeValue);
                    }
                  } else {
                    onUndercutChange(undefined, undefined, null);
                  }
                }}
              />
            </PosFormField>
          </Stack>

          <div className={styles.inlinePricingInputText}>
            <Content id={ContentId.Then} />
          </div>
          <Stack width="full" gap="m">
            <ToggleGroup
              disabled={!undercutMode || !undercutAbsoluteAmount}
              options={UNDERCUT_PRICE_MODE_OPTIONS}
              value={undercutAbsoluteAmountMode}
              onValueChange={(value) => {
                if (!value) return;

                const mode = value as UndercutPriceSettingsMode;
                setUndercutAbsoluteAmountMode(mode);

                if (undercutAbsoluteAmount?.value) {
                  const curAmountAbs = Math.abs(undercutAbsoluteAmount.value);
                  onUndercutChange(
                    undefined,
                    getUndercutPriceRepresentingMode(curAmountAbs, mode)
                  );
                }
              }}
            />
            <div className={styles.inlinePricingInputText}>
              <Content id={ContentId.By} />
            </div>
            <PosFormField style={{ width: 'fit-content' }}>
              <PosCurrencyField
                uiCurrency={uiCurrency}
                disabled={!undercutMode}
                value={
                  undercutAbsoluteAmount?.value != null
                    ? Math.abs(undercutAbsoluteAmount.value)
                    : ''
                }
                placeholder={undefined}
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  if (v >= 0 && v <= Number.MAX_VALUE) {
                    if (
                      undercutAbsoluteAmountMode ===
                      UndercutPriceSettingsMode.Premium
                    ) {
                      onUndercutChange(undefined, -v);
                    } else {
                      onUndercutChange(undefined, v);
                    }
                  } else {
                    onUndercutChange(undefined, null);
                  }
                }}
              />
            </PosFormField>
          </Stack>
        </Stack>
      ),
    });

    return settings;
  }, [
    onResetUndercutAmount,
    onResetUndercutMode,
    onUndercutChange,
    onUndercutModeChange,
    setUndercutAbsoluteAmountMode,
    setUndercutRelativeAmountMode,
    settingsInput.undercutAbsoluteAmount?.hasConflict,
    settingsInput.undercutMode?.hasConflict,
    settingsInput.undercutReferenceListingPosition?.hasConflict,
    settingsInput.undercutRelativeAmount?.hasConflict,
    showReferenceListingPositionInput,
    uiCurrency,
    undercutAbsoluteAmount,
    undercutAbsoluteAmountMode,
    undercutAmountHasChange,
    undercutMode,
    undercutModeHasChange,
    undercutReferenceListingPosition?.value,
    undercutRelativeAmount?.value,
    undercutRelativeAmountMode,
  ]);

  return (
    <Stack direction="column" gap="s" alignItems="start" width="full">
      {hasConflict && (
        <Stack
          width="full"
          direction="row"
          gap="m"
          className={styles.conflictAlert}
        >
          <WarningOutlineIcon size={vars.iconSize.l} />
          <Content id={ContentId.PricingSettingsListingsConflictWarning} />
        </Stack>
      )}
      <Stack width="full" className={styles.groupedContainer}>
        <MobilePricingSetting
          header={header}
          hasChange={hasChange}
          hasConflict={hasAutoPricingEnableConflict}
          onReset={onReset}
          details={settings}
        />
      </Stack>
      <MobilePricingSettingsGroup
        header={<Content id={ContentId.Undercutting} />}
        settings={undercuttingSettings}
      />
    </Stack>
  );
};

type MobilePricingSettingsGroupProps = {
  header: React.ReactNode;
  settings: MobilePricingSettingProps[];
};
const MobilePricingSettingsGroup: React.FC<MobilePricingSettingsGroupProps> = ({
  header,
  settings,
}) => {
  return (
    <Stack
      direction="column"
      width="full"
      gap="m"
      className={styles.groupedContainer}
    >
      <div className={styles.groupedHeader}>{header}</div>
      {settings.map((setting, index) => {
        return <MobilePricingSetting key={index} {...setting} />;
      })}
    </Stack>
  );
};

type MobilePricingSettingProps = {
  header: React.ReactNode;
  details: React.ReactNode;
  hasChange?: boolean;
  hasConflict?: boolean;
  onReset?: () => void;
};

const MobilePricingSetting: React.FC<MobilePricingSettingProps> = ({
  header,
  details,
  hasChange,
  hasConflict,
  onReset,
}) => {
  return (
    <Stack direction="column" gap="m" width="full" className={styles.container}>
      <Stack
        direction="row"
        justifyContent="spaceBetween"
        alignItems="center"
        width="full"
      >
        <div>{header}</div>
        <PricingSettingStatus
          hasChange={hasChange}
          hasConflict={hasConflict}
          onReset={onReset}
        />
      </Stack>
      <div className={styles.mobilePricingSettingsDetails}>{details}</div>
    </Stack>
  );
};
